import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import V2Nav from '../../components/homepage-v2/V2Nav';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { COMPANY, companyRegistrationLine } from '../../constants/company';
import '../../components/homepage-v2/homepageV2.css';

export default function Privacy() {
  usePageMeta(
    'Privacy Policy | SmileConsent',
    'How SmileConsent collects, uses, and protects personal data for dental practices and patients. GDPR, cookies, third-party processors, and your rights.',
    'https://smileconsent.co.uk/privacy-policy'
  );

  return (
    <div className="hpv2 min-h-screen">
      <V2Nav />
      <div className="py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                SmileConsent (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is operated by {COMPANY.legalName}. {companyRegistrationLine()} We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our dental consent management platform.
              </p>
              <p className="text-gray-700 mb-4">
                By using our service, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our service.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">We may collect the following types of personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Contact information (name, email address, phone number)</li>
                <li>Professional information (GDC number, practice details, qualifications)</li>
                <li>Practice information (clinic name, address, registration details)</li>
                <li>Patient consent data (when acting as a data processor)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Usage data and analytics</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Technical Information</h3>
              <p className="text-gray-700 mb-4">We automatically collect certain technical information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent</li>
                <li>Error logs and performance data</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Providing and maintaining our dental consent management service</li>
                <li>Processing payments and managing subscriptions</li>
                <li>Communicating with you about service updates and support</li>
                <li>Improving our platform and user experience</li>
                <li>Ensuring compliance with legal and regulatory requirements</li>
                <li>Preventing fraud and ensuring security</li>
                <li>Providing customer support and technical assistance</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p className="text-gray-700 mb-4">Under GDPR, we process your personal data based on the following legal grounds:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Contract:</strong> To provide our services as agreed in our terms</li>
                <li><strong>Legitimate Interest:</strong> To improve our services and ensure security</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
                <li><strong>Consent:</strong> Where you have given explicit consent for specific purposes</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our platform</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>Consent:</strong> With your explicit consent for specific purposes</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 mb-4">We implement appropriate technical and organizational security measures to protect your personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure hosting infrastructure (Firebase/Google Cloud)</li>
                <li>Regular backups and disaster recovery procedures</li>
                <li>Staff training on data protection practices</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 mb-4">We retain your personal information only for as long as necessary to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide our services</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain business records as required by law</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Patient consent data is retained according to dental practice requirements and legal obligations. We will delete or anonymize your data when it is no longer needed.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights (GDPR)</h2>
              <p className="text-gray-700 mb-4">Under GDPR, you have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Restriction:</strong> Request limitation of processing</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Our services are hosted on Google Cloud infrastructure, which may involve data transfers outside the European Economic Area (EEA). We ensure that such transfers comply with GDPR requirements through appropriate safeguards, including Standard Contractual Clauses and adequacy decisions.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze website usage and performance</li>
                <li>Provide personalized content and features</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can control cookie settings through your browser preferences. However, disabling certain cookies may affect the functionality of our service.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">Our platform integrates with the following third-party services:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Stripe:</strong> Payment processing (subject to Stripe's privacy policy)</li>
                <li><strong>Firebase/Google Cloud:</strong> Hosting and infrastructure</li>
                <li><strong>SendGrid:</strong> Email delivery services</li>
                <li><strong>Cloudinary:</strong> Media storage and processing</li>
              </ul>
              <p className="text-gray-700 mb-4">
                These services have their own privacy policies, and we recommend reviewing them.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date. Your continued use of our service after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Company:</strong> {COMPANY.legalName}</p>
                <p className="text-gray-700 mb-2"><strong>Company number:</strong> {COMPANY.number}</p>
                <p className="text-gray-700 mb-2"><strong>Email:</strong> info@smileconsent.co.uk</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Complaints</h2>
              <p className="text-gray-700 mb-4">
                If you believe we have not addressed your concerns satisfactorily, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) in the UK:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>ICO Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://ico.org.uk</a></p>
                <p className="text-gray-700 mb-2"><strong>ICO Helpline:</strong> 0303 123 1113</p>
              </div>
            </div>

            <div className="text-center mt-12 mb-8">
              <CompanyDisclosure tone="light" />
            </div>
            <div className="text-center mt-12">
              <Link 
                to="/" 
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
